/**
 * QuickBooks API Slice
 *
 * RTK Query API slice for QuickBooks integration endpoints.
 * Handles OAuth connection, file parsing, receipt uploads, and configuration.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  QuickBooksConnection,
  AuthorizationURL,
  ParsedTransactions,
  ParseFileRequest,
  UploadReceiptsRequest,
  UploadHistory,
  QuickBooksConfig,
  UpdateConfigRequest,
  UndoUploadResult,
  AsyncUploadResponse,
  UploadProgress,
} from '../types/quickbooks';

export const quickbooksApi = createApi({
  reducerPath: 'quickbooksApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['QuickBooksConnection', 'QuickBooksConfig', 'UploadHistory'],
  endpoints: builder => ({
    // ========================================================================
    // Connection Endpoints
    // ========================================================================

    /**
     * Initiate QuickBooks OAuth connection
     * Returns authorization URL to redirect user to
     */
    initiateConnection: builder.mutation<AuthorizationURL, string>({
      query: studioId => ({
        url: `/studios/${studioId}/quickbooks/connect/`,
        method: 'POST',
      }),
      invalidatesTags: ['QuickBooksConnection'],
    }),

    /**
     * Get QuickBooks connection status for a studio
     */
    getConnectionStatus: builder.query<QuickBooksConnection, string>({
      query: studioId => `/studios/${studioId}/quickbooks/status/`,
      providesTags: ['QuickBooksConnection'],
    }),

    /**
     * Disconnect QuickBooks integration
     */
    disconnectQuickBooks: builder.mutation<{ message: string }, string>({
      query: studioId => ({
        url: `/studios/${studioId}/quickbooks/disconnect/`,
        method: 'POST',
      }),
      invalidatesTags: ['QuickBooksConnection', 'UploadHistory'],
    }),

    // ========================================================================
    // File Processing Endpoints
    // ========================================================================

    /**
     * Parse uploaded Excel file
     * Returns grouped transactions by payment type
     */
    parseFile: builder.mutation<ParsedTransactions, ParseFileRequest>({
      query: ({ studioId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/studios/${studioId}/quickbooks/parse-file/`,
          method: 'POST',
          body: formData,
        };
      },
    }),

    /**
     * Check for duplicate sales receipts before upload
     * Compares against local database records from last 90 days
     */
    checkDuplicates: builder.mutation<
      {
        duplicates: Array<{
          transaction: any;
          previous_upload: {
            upload_id: string;
            uploaded_at: string;
            file_name: string;
            quickbooks_id: string;
          };
        }>;
        new: any[];
        summary: {
          total_checked: number;
          duplicates_found: number;
          new_transactions: number;
        };
      },
      { studioId: string; grouped_transactions: Record<string, any[]> }
    >({
      query: ({ studioId, grouped_transactions }) => ({
        url: `/studios/${studioId}/quickbooks/check-duplicates/`,
        method: 'POST',
        body: { grouped_transactions },
      }),
    }),

    /**
     * Upload sales receipts to QuickBooks (Async)
     * Returns task ID for progress tracking
     */
    uploadReceipts: builder.mutation<
      AsyncUploadResponse,
      UploadReceiptsRequest
    >({
      query: ({
        studioId,
        payment_types,
        grouped_transactions,
        file_name,
      }) => ({
        url: `/studios/${studioId}/quickbooks/upload-receipts/`,
        method: 'POST',
        body: {
          payment_types,
          grouped_transactions,
          file_name,
        },
      }),
      invalidatesTags: ['UploadHistory'],
    }),

    /**
     * Get upload progress for async task
     * Poll this endpoint to track upload progress
     */
    getUploadProgress: builder.query<
      UploadProgress,
      { studioId: string; taskId: string }
    >({
      query: ({ studioId, taskId }) =>
        `/studios/${studioId}/quickbooks/upload-progress/${taskId}/`,
    }),

    // ========================================================================
    // History & Configuration Endpoints
    // ========================================================================

    /**
     * Get upload history for a studio
     */
    getUploadHistory: builder.query<UploadHistory[], string>({
      query: studioId => `/studios/${studioId}/quickbooks/upload-history/`,
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'UploadHistory' as const,
                id,
              })),
              { type: 'UploadHistory', id: 'LIST' },
            ]
          : [{ type: 'UploadHistory', id: 'LIST' }],
    }),

    /**
     * Get QuickBooks configuration for a studio
     */
    getConfig: builder.query<QuickBooksConfig, string>({
      query: studioId => `/studios/${studioId}/quickbooks/config/`,
      providesTags: ['QuickBooksConfig'],
    }),

    /**
     * Update QuickBooks configuration
     */
    updateConfig: builder.mutation<QuickBooksConfig, UpdateConfigRequest>({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/quickbooks/config/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['QuickBooksConfig'],
    }),

    /**
     * Undo a sales receipt upload
     * Deletes all receipts from QuickBooks
     */
    undoUpload: builder.mutation<
      UndoUploadResult,
      { studioId: string; uploadId: string }
    >({
      query: ({ studioId, uploadId }) => ({
        url: `/studios/${studioId}/quickbooks/undo-upload/${uploadId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UploadHistory'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useInitiateConnectionMutation,
  useGetConnectionStatusQuery,
  useLazyGetConnectionStatusQuery,
  useDisconnectQuickBooksMutation,
  useParseFileMutation,
  useCheckDuplicatesMutation,
  useUploadReceiptsMutation,
  useLazyGetUploadProgressQuery,
  useGetUploadHistoryQuery,
  useGetConfigQuery,
  useUpdateConfigMutation,
  useUndoUploadMutation,
} = quickbooksApi;
